import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { API_BASE_URL, API_TIMEOUT } from "@/config/env";
import TokenManager from "./tokenManager";
import { parseTokenResponse } from "./authMappers";
import { RequestConfig } from "@/services/types/common.types";
import type { RefreshTokenRequest } from "@/services/types/auth.types";

/**
 * Custom error class for API errors
 */
export class ApiException extends Error {
  status: number;
  code?: string;
  errors?: Record<string, string[]>;
  details?: unknown;
  originalError?: unknown;

  constructor(
    message: string,
    status: number = 500,
    code?: string,
    errors?: Record<string, string[]>,
    originalError?: unknown,
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiException";
    this.status = status;
    this.code = code;
    this.errors = errors;
    this.originalError = originalError;
    this.details = details;
    Object.setPrototypeOf(this, ApiException.prototype);
  }
}

/**
 * Axios Instance Configuration
 */
class AxiosInstanceManager {
  private instance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (error?: unknown) => void;
  }> = [];

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request Interceptor
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Skip auth header if skipAuth is set
        const extendedConfig = config as InternalAxiosRequestConfig & RequestConfig;
        if (extendedConfig.skipAuth) {
          return config;
        }

        // Add access token to headers
        const accessToken = TokenManager.getAccessToken();
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }

        return config;
      },
      (error: unknown) => {
        return Promise.reject(error);
      },
    );

    // Response Interceptor
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error: unknown) => {
        if (!axios.isAxiosError(error)) {
          return Promise.reject(this.handleError(error));
        }
        const axiosError = error as AxiosError;
        const originalRequest = axiosError.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
          skipAuth?: boolean;
          skipRefresh?: boolean;
        };

        // Skip refresh if explicitly set or if it's an auth endpoint
        if (originalRequest?.skipRefresh || originalRequest?.skipAuth) {
          return Promise.reject(this.handleError(axiosError));
        }

        // Handle 401 Unauthorized - Token expired or invalid
        if (axiosError.response?.status === 401 && !originalRequest?._retry) {
          // Check if access token is expired
          if (TokenManager.isAccessTokenExpired()) {
            return this.handleTokenRefresh(originalRequest, axiosError);
          }

          // If token is not expired but still getting 401, it might be invalid
          // Try to refresh anyway
          return this.handleTokenRefresh(originalRequest, axiosError);
        }

        // Handle other errors
        return Promise.reject(this.handleError(axiosError));
      },
    );
  }

  /**
   * Handle token refresh logic
   * OPTIMIZED: Direct refresh without callbacks
   */
  private async handleTokenRefresh(
    originalRequest: InternalAxiosRequestConfig & { _retry?: boolean },
    _error: unknown,
  ): Promise<unknown> {
    // If already refreshing, queue the request
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      })
        .then(() => {
          // Retry original request with new token
          const accessToken = TokenManager.getAccessToken();
          if (accessToken && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }
          return this.instance(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;
    this.isRefreshing = true;

    const refreshToken = TokenManager.getRefreshToken();

    // If no refresh token, logout user
    if (!refreshToken) {
      this.isRefreshing = false;
      this.processQueue(null);
      this.performLogout();
      return Promise.reject(
        new ApiException("Session expired. Please login again.", 401),
      );
    }

    try {
      // OPTIMIZED: Call refresh API directly
      const { API_ENDPOINTS } = await import("./endpoints");

      const refreshUrl = `${API_BASE_URL}/${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`;

      const refreshBody: RefreshTokenRequest = { refresh_token: refreshToken };
      const response = await axios.post(refreshUrl, refreshBody);

      // Accepts the snake_case flat shape as well as the old camelCase / tokens-wrapped shapes
      const tokens = parseTokenResponse(response.data);
      if (!tokens) {
        throw new Error("Token refresh failed: unexpected server response");
      }

      // Update tokens
      TokenManager.setTokens(tokens);

      // Process queued requests
      this.processQueue(null);

      // Retry original request with new token
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
      }

      return this.instance(originalRequest);
    } catch (refreshError) {
      // Refresh failed
      this.processQueue(refreshError);

      const status = axios.isAxiosError(refreshError)
        ? refreshError.response?.status
        : undefined;

      // If refresh explicitly says tokens are invalid/unauthorized, logout
      if (status === 401 || status === 403) {
        this.performLogout();
        return Promise.reject(
          new ApiException("Session expired. Please login again.", 401),
        );
      }

      // For other errors (5xx, network, etc.), DON'T logout automatically.
      // Surface the formatted error and let the app decide how to handle it.
      return Promise.reject(this.handleError(refreshError));
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * OPTIMIZED: Direct logout without callbacks
   * Uses window.location for clean state reset
   */
  private performLogout(): void {
    // Clear tokens
    TokenManager.clearTokens();

    // Clear Zustand store using dynamic import to avoid circular deps
    import("@/store")
      .then(({ useAuthStore }) => {
        useAuthStore.getState().logout();
      })
      .catch(console.error);
  }

  /**
   * Process queued requests after token refresh
   */
  private processQueue(error: unknown): void {
    this.failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve();
      }
    });
    this.failedQueue = [];
  }

  /**
   * Handle and format API errors
   */
  private handleError(error: unknown): ApiException {
    // Network or non-HTTP error
    if (!axios.isAxiosError(error)) {
      return new ApiException(
        error instanceof Error
          ? error.message
          : "Network error. Please check your connection.",
        0,
        "NETWORK_ERROR",
        undefined,
        error,
      );
    }

    const { status, data } = error.response ?? {};
    const responseData = data as Record<string, unknown> | undefined;
    const message =
      (responseData?.message as string | undefined) ||
      ((responseData?.error as Record<string, unknown> | undefined)
        ?.message as string | undefined) ||
      (responseData?.error as string | undefined) ||
      error.message ||
      "An error occurred. Please try again.";

    const details = responseData?.details as
      | { fieldErrors?: Record<string, string[]>; formErrors?: string[] }
      | undefined;
    const errors =
      (responseData?.errors as Record<string, string[]> | undefined) ||
      details?.fieldErrors ||
      (responseData?.fieldErrors as Record<string, string[]> | undefined);

    return new ApiException(
      message,
      status ?? 500,
      responseData?.code as string | undefined,
      errors,
      error,
      responseData?.details,
    );
  }

  /**
   * Get the axios instance
   */
  getInstance(): AxiosInstance {
    return this.instance;
  }
}

// Create singleton instance
const axiosInstanceManager = new AxiosInstanceManager();
export const axiosInstance = axiosInstanceManager.getInstance();

export default axiosInstance;
